/**
 * Hangs artworks in a list at one shared height, as on a gallery wall: every work's
 * top and bottom edges line up, and each takes a width that follows its own
 * proportions (a landscape wider, a portrait narrower), never narrower than its
 * text needs. The shared height is the --fifi-wall-height custom property of the
 * list. Lists are the homepage rails, the walls under Journal stories
 * ([data-fifi-hang]), artwork grids and the artist page grid.
 * The picture's own proportions decide its shape. Without this script the lists
 * keep their regular frames.
 *
 * Collection and search grids keep equal columns, as in the reference gallery: each
 * work fills its column in its own proportions, and this script lines the picture
 * bottoms up along every row so the captions start on one line. Without it the
 * pictures simply sit at the top of their row.
 */

const ITEM = '.fifi-art-card__media, .card-gallery';
const GROUP = '.fifi-rail__track, [data-fifi-hang], .fifi-artwork-grid__list, .fifi-profile__grid';
const COLUMNS = '.product-grid';
const MIN_TEXT_WIDTH = 150;

let frame = 0;

function queue() {
  cancelAnimationFrame(frame);
  frame = requestAnimationFrame(apply);
}

/**
 * The picture's width-to-height ratio, or null until it has loaded.
 * @param {HTMLElement} element
 */
function aspectOf(element) {
  const image = element.querySelector('img');
  if (!image) return null;
  if (!image.naturalWidth || !image.naturalHeight) {
    // Not loaded yet (lazy or still decoding): measure again once it arrives
    image.addEventListener('load', queue, { once: true });
    return null;
  }
  return image.naturalWidth / image.naturalHeight;
}

/**
 * The list's shared height in pixels, read from --fifi-wall-height.
 * @param {HTMLElement} group
 */
function wallHeight(group) {
  const probe = document.createElement('span');
  probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;width:0;height:var(--fifi-wall-height, 22rem)';
  group.append(probe);
  const height = probe.getBoundingClientRect().height;
  probe.remove();
  return height;
}

function apply() {
  for (const group of document.querySelectorAll(GROUP)) {
    if (group instanceof HTMLElement) hang(group);
  }
  for (const grid of document.querySelectorAll(COLUMNS)) {
    if (grid instanceof HTMLElement) lineUp(grid);
  }
}

/**
 * Pushes each picture down so the bottoms of all pictures in a row line up.
 * @param {HTMLElement} grid
 */
function lineUp(grid) {
  const pictures = [];
  for (const item of grid.children) {
    const picture = item instanceof HTMLElement ? item.querySelector('.card-gallery') : null;
    if (picture instanceof HTMLElement) {
      picture.style.removeProperty('margin-block-start');
      pictures.push({ item, picture });
    }
  }
  // The theme's zoomed-out view is a plain overview: leave its pictures at the top
  if (grid.getAttribute('product-grid-view') === 'zoom-out') return;

  /** @type {Map<number, {picture: HTMLElement, height: number}[]>} */
  const rows = new Map();
  for (const { item, picture } of pictures) {
    const top = Math.round(item.getBoundingClientRect().top);
    const row = rows.get(top) ?? [];
    row.push({ picture, height: picture.getBoundingClientRect().height });
    rows.set(top, row);
  }

  for (const row of rows.values()) {
    const tallest = Math.max(...row.map((entry) => entry.height));
    for (const { picture, height } of row) {
      if (tallest - height > 0.5) picture.style.marginBlockStart = `${(tallest - height).toFixed(1)}px`;
    }
  }
}

/** @param {HTMLElement} group */
function hang(group) {
  const items = Array.from(group.children).filter((child) => child instanceof HTMLElement && child.querySelector(ITEM));
  if (!items.length || !group.clientWidth) return;

  const height = wallHeight(group);
  if (!height) return;
  const widest = group.clientWidth;

  for (const item of items) {
    const frameElement = /** @type {HTMLElement} */ (item.querySelector(ITEM));
    const aspect = aspectOf(frameElement) ?? 0.8;
    const artWidth = Math.min(height * aspect, widest);
    const artHeight = artWidth / aspect;
    const slideWidth = Math.min(Math.max(artWidth, MIN_TEXT_WIDTH), widest);

    item.style.setProperty('--fifi-slide-width', `${Math.round(slideWidth)}px`);
    frameElement.style.setProperty('--fifi-art-width', `${artWidth.toFixed(1)}px`);
    frameElement.style.setProperty('--fifi-art-height', `${artHeight.toFixed(1)}px`);
    if (frameElement.classList.contains('card-gallery')) {
      // Theme product cards size their picture from this ratio: one height for all
      frameElement.style.setProperty('--gallery-aspect-ratio', (slideWidth / height).toFixed(4));
    }
  }
  group.setAttribute('data-fifi-wall', '');
}

document.addEventListener(
  'load',
  (event) => {
    if (event.target instanceof HTMLImageElement && event.target.closest(ITEM)) queue();
  },
  true
);
window.addEventListener('resize', queue, { passive: true });
window.addEventListener('load', queue, { once: true });
new MutationObserver((records) => {
  // Re-hang when list items arrive (filters, pagination), not for the measuring probe
  const added = records.some((record) => Array.from(record.addedNodes).some((node) => node instanceof HTMLElement && node.tagName !== 'SPAN'));
  if (added) queue();
}).observe(document.body, { childList: true, subtree: true });
queue();
